import { ContactAvatar } from "@/components/ContactAvatar/ContactAvatar";
import { useTranslation } from "react-i18next";
import ConfigDesks from "@/services/configDesks";
import { IconEdit } from "@/assets/images/Icons";
import { EditarContato } from "@/pages/UserGoOn/Contatos/components/EditarContato";
import { useRef, useState, type MouseEvent } from "react";

type ContactInfo = {
  id?: number | string;
  name?: string;
  last_name?: string;
  phone_number?: string;
  additional_phones?: string | null;
  email?: string;
  additional_emails?: string | null;
  bio?: string;
  city?: string;
  [key: string]: unknown;
};

type ExtraContact = {
  value?: string;
  is_verified?: boolean;
};

type InformationContatoProps = {
  data?: unknown;
  id?: number | string;
  name?: string;
  foto?: string | null;
};

export function InformationContato({
  data,
  id,
  name,
  foto,
}: InformationContatoProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [contactData, setContactData] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [popoverPos, setPopoverPos] = useState({
    top: 0,
    left: 0,
    width: 256,
  });

  const [editContato, setEditContato] = useState(false);

  const getUser = async () => {
    const userId = Number(id);
    if (!Number.isFinite(userId)) return;

    setLoading(true);
    try {
      const res = await ConfigDesks.getUserById(userId);
      setContactData(res.data as ContactInfo);
    } catch (error) {
      console.error(t("utils.contactInfo.fetchError"), error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (event: MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const width = Math.min(256, window.innerWidth - 16);
      const viewportGap = 8;
      const availableRight = window.innerWidth - rect.right - viewportGap;
      const preferredLeft =
        availableRight >= width
          ? rect.right + viewportGap
          : rect.left - width - viewportGap;
      const left = Math.min(
        Math.max(viewportGap, preferredLeft),
        window.innerWidth - width - viewportGap,
      );
      const top = Math.min(
        Math.max(viewportGap, rect.top),
        Math.max(viewportGap, window.innerHeight - 280),
      );
      setPopoverPos({
        top,
        left,
        width,
      });
    }
    setEditContato(false);
    setShowInfo(true);
    getUser();
  };

  const closeInfos = (event?: MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setEditContato(false);
    setShowInfo(false);
  };

  return (
    <div className="relative">
      <span
        ref={triggerRef}
        title={t("utils.contactInfo.clickToSeeMore")}
        onClick={handleOpen}
        onMouseDown={(event) => event.stopPropagation()}
        className="cursor-pointer truncate max-w-full block"
      >
        {name}
      </span>

      {showInfo && (
        <>
          <div
            className="fixed bg-black/10 top-0 bottom-0 right-0 left-0 z-[70]"
            onClick={closeInfos}
          ></div>
          <div
            style={{
              position: "fixed",
              top: popoverPos.top,
              left: popoverPos.left,
              width: popoverPos.width,
            }}
            className="z-[80] max-h-[calc(100vh-16px)] overflow-y-auto rounded-xl border border-cinza-200 bg-white p-4 shadow-lg"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {loading ? (
              <div className="space-y-2 animate-pulse">
                <div className="flex items-center gap-2 pb-1">
                  <div className="w-8 h-8 bg-gray-300 rounded-md"></div>
                  <div className="flex flex-col space-y-1">
                    <div className="w-24 h-4 bg-gray-300 rounded"></div>
                    <div className="w-16 h-4 bg-gray-300 rounded"></div>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="w-full h-4 bg-gray-300 rounded"></div>
                  <div className="w-full h-4 bg-gray-300 rounded"></div>
                  <div className="w-full h-4 bg-gray-300 rounded"></div>
                  <div className="w-full h-4 bg-gray-300 rounded"></div>
                  <div className="w-full h-4 bg-gray-300 rounded"></div>
                </div>
              </div>
            ) : contactData ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 pb-1">
                  <ContactAvatar
                    src={foto}
                    name={contactData.name}
                    lastName={contactData.last_name}
                    className="w-8 h-8 rounded-md"
                    textClassName="text-xs font-semibold"
                  />
                  <p className="min-w-0 break-words text-sm font-semibold leading-tight text-cinza-900">
                    {contactData.name} {contactData.last_name}
                  </p>
                </div>
                <p className="flex flex-col text-xs leading-snug text-cinza-800">
                  <strong className="text-black">
                    {t("utils.contactInfo.phone")}
                  </strong>
                  <span className="break-words">{contactData.phone_number}</span>
                </p>
                {contactData.additional_phones &&
                  (() => {
                    try {
                      const phones = JSON.parse(
                        contactData.additional_phones
                      ) as ExtraContact[];
                      return phones.length > 0
                        ? phones.map((p, i) => (
                            <p
                              key={i}
                              className="flex items-center gap-1 text-xs text-black/70"
                            >
                              <span className="break-words">{p.value}</span>
                              {p.is_verified && (
                                <span className="text-green-500 text-xs font-semibold">
                                  ✓
                                </span>
                              )}
                            </p>
                          ))
                        : null;
                    } catch {
                      return null;
                    }
                  })()}
                <p className="flex flex-col text-xs leading-snug text-cinza-800">
                  <strong className="text-black">
                    {t("utils.contactInfo.email")}
                  </strong>
                  <span className="break-all">{contactData.email}</span>
                </p>
                {contactData.additional_emails &&
                  (() => {
                    try {
                      const emails = JSON.parse(
                        contactData.additional_emails
                      ) as ExtraContact[];
                      return emails.length > 0
                        ? emails.map((e, i) => (
                            <p
                              key={i}
                              className="flex items-center gap-1 text-xs text-black/70"
                            >
                              <span className="break-all">{e.value}</span>
                              {e.is_verified && (
                                <span className="text-green-500 text-xs font-semibold">
                                  ✓
                                </span>
                              )}
                            </p>
                          ))
                        : null;
                    } catch {
                      return null;
                    }
                  })()}
                <p className="flex flex-col text-xs leading-snug text-cinza-800">
                  <strong className="text-black">
                    {t("utils.contactInfo.bio")}
                  </strong>
                  <span className="break-words">{contactData.bio}</span>
                </p>
                <p className="flex flex-col text-xs leading-snug text-cinza-800">
                  <strong className="text-black">
                    {t("utils.contactInfo.city")}
                  </strong>
                  <span className="break-words">{contactData.city}</span>
                </p>
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    title={t("utils.contactInfo.edit")}
                    className="flex h-9 w-9 items-center justify-center rounded-xl bg-azul-400 text-white transition-colors hover:bg-azul-400/90"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setEditContato(true);
                      setShowInfo(false);
                    }}
                  >
                    <IconEdit />
                  </button>
                </div>
              </div>
            ) : (
              <p>{t("utils.contactInfo.loadError")}</p>
            )}
          </div>
        </>
      )}
      {editContato && (
        <EditarContato
          isOpen={editContato}
          setIsOpen={setEditContato}
          infoContato={contactData ?? data}
          closeInfoContato={() => setEditContato(false)}
        />
      )}
    </div>
  );
}
